import { type ReactNode, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import { colors, radius, shadows } from '@gto/ui/theme/design-tokens'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import { StatsCard } from '@/components/stats-card'

export type CampaignChartPoint = {
  label: string
  revenue: number
  commissions: number
  cumulativeRevenue: number
  cumulativeCommissions: number
  orders: number
  conversions: number
}

/**
 * Which pair of series the chart is showing. Three questions the same buckets
 * answer, rather than three cards: whether the money grew, whether the volume
 * grew (a campaign can post flat revenue on rising orders — that is a price
 * story, not a demand story), and where the campaign stands against its own
 * running total.
 */
const CHART_MODES = ['money', 'volume', 'cumulative'] as const
type ChartMode = (typeof CHART_MODES)[number]

const isChartMode = (value: string): value is ChartMode =>
  (CHART_MODES as readonly string[]).includes(value)

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

const formatCount = (value: number) => value.toLocaleString('en-US')

type SeriesSpec = {
  key: keyof Omit<CampaignChartPoint, 'label'>
  color: string
  labelId: string
}

/**
 * Revenue keeps the primary accent; its partner series is `info` blue rather
 * than `success` green. The two greens the palette offers (`primary.main`
 * #aafbb2 and `success.main` #66bb6a) are the same hue family, so a legend dot
 * was the only thing telling the lines apart.
 */
const MODE_SERIES: Record<ChartMode, SeriesSpec[]> = {
  money: [
    {
      key: 'revenue',
      color: colors.primary.main,
      labelId: 'admin.partnerDetail.campaigns.col.revenue',
    },
    {
      key: 'commissions',
      color: colors.info.main,
      labelId: 'admin.partnerDetail.campaigns.col.commission',
    },
  ],
  volume: [
    {
      key: 'orders',
      color: colors.primary.main,
      labelId: 'admin.partnerDetail.campaigns.col.orders',
    },
    {
      key: 'conversions',
      color: colors.info.main,
      labelId: 'admin.partnerDetail.campaigns.col.converted',
    },
  ],
  cumulative: [
    {
      key: 'cumulativeRevenue',
      color: colors.primary.main,
      labelId: 'admin.partnerDetail.campaigns.col.revenue',
    },
    {
      key: 'cumulativeCommissions',
      color: colors.info.main,
      labelId: 'admin.partnerDetail.campaigns.col.commission',
    },
  ],
}

/** The swatch mirrors the mark it stands for, so the legend reads without a key. */
const SWATCH_CLASS_NAME = {
  bar: 'h-3 w-2.5 shrink-0 rounded-xs',
  line: 'h-1 w-4 shrink-0 rounded-full',
} as const

type ChartLegendProps = {
  color: string
  label: string
  shape: keyof typeof SWATCH_CLASS_NAME
}

const ChartLegend = ({ color, label, shape }: ChartLegendProps) => (
  <div className="flex items-center gap-1.5">
    <span
      aria-hidden
      className={SWATCH_CLASS_NAME[shape]}
      style={{ backgroundColor: color }}
    />
    <Typography
      color="text.secondary"
      variant="caption"
    >
      {label}
    </Typography>
  </div>
)

type CampaignChartProps = {
  title: ReactNode
  data: CampaignChartPoint[]
  /** The granularity toggle, rendered beside this chart's own mode toggle. */
  controls?: ReactNode
  emptyLabel?: ReactNode
  height?: number
}

export const CampaignChart = ({
  title,
  data,
  controls,
  emptyLabel,
  height = 260,
}: CampaignChartProps) => {
  const { formatMessage } = useIntl()
  const [mode, setMode] = useState<ChartMode>('money')

  const series = MODE_SERIES[mode]
  const isMoney = mode !== 'volume'
  const isBars = mode === 'volume'
  const isEmpty =
    data.length === 0 || data.every((point) => series.every((s) => point[s.key] === 0))

  return (
    <StatsCard
      icon={<PresentationChartLineIcon />}
      title={title}
      controls={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <div className="flex items-center gap-3">
            {series.map((spec) => (
              <ChartLegend
                key={spec.key}
                color={spec.color}
                label={formatMessage({ id: spec.labelId })}
                shape={isBars ? 'bar' : 'line'}
              />
            ))}
          </div>
          <ToggleButtonGroup
            size="small"
            value={mode}
            onValueChange={(value) => {
              if (isChartMode(value)) {
                setMode(value)
              }
            }}
          >
            {CHART_MODES.map((value) => (
              <ToggleButton
                key={value}
                value={value}
              >
                {formatMessage({ id: `admin.partnerDetail.campaigns.chart.mode.${value}` })}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {controls}
        </div>
      }
    >
      <div className="relative">
        {emptyLabel != null && isEmpty && (
          <Typography
            className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center"
            color="text.secondary"
            component="div"
            data-tst="chart-empty"
            variant="caption"
          >
            {emptyLabel}
          </Typography>
        )}
        <ResponsiveContainer
          height={height}
          width="100%"
        >
          {/* left: 0 (not the -16 the other charts use) and an explicit axis
              width: a campaign's totals reach five figures, and "$12,000" is
              clipped at the negative margin those smaller-scale charts get
              away with. */}
          <ComposedChart
            data={data}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
          >
            <defs>
              {series.map((spec) => (
                <linearGradient
                  key={spec.key}
                  id={`campaignFill-${spec.key}`}
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={spec.color}
                    stopOpacity={0.18}
                  />
                  <stop
                    offset="95%"
                    stopColor={spec.color}
                    stopOpacity={0}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid
              stroke={colors.divider}
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              axisLine={{ stroke: colors.divider }}
              dataKey="label"
              interval="preserveStartEnd"
              tick={{ fill: colors.text.secondary, fontSize: 11 }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              domain={[0, 'auto']}
              tick={{ fill: colors.text.secondary, fontSize: 11 }}
              tickFormatter={isMoney ? formatCurrency : formatCount}
              tickLine={false}
              width={isMoney ? 64 : 44}
            />
            <Tooltip
              cursor={{ fill: colors.text.hover }}
              contentStyle={{
                backgroundColor: colors.paper.elevation8,
                border: `1px solid ${colors.divider}`,
                borderRadius: Number(radius.sm.replace('px', '')),
                fontSize: 13,
                boxShadow: shadows.elevation8,
              }}
              formatter={(value: number, name: string) => {
                const spec = series.find((s) => s.key === name)

                return [
                  isMoney ? formatCurrency(value) : formatCount(value),
                  spec ? formatMessage({ id: spec.labelId }) : name,
                ]
              }}
            />
            {series.map((spec) =>
              isBars ? (
                <Bar
                  key={spec.key}
                  dataKey={spec.key}
                  fill={spec.color}
                  fillOpacity={0.75}
                  radius={[2, 2, 0, 0]}
                />
              ) : (
                <Area
                  key={spec.key}
                  dataKey={spec.key}
                  fill={`url(#campaignFill-${spec.key})`}
                  stroke={spec.color}
                  strokeWidth={2}
                  type="monotone"
                />
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </StatsCard>
  )
}
